import React from 'react';
import { Col, Form, Input, Row, Select, Switch } from 'antd';

import {
	PatientNameContext,
	PatientOtherNameSubmission,
} from '../../../../types/healthcare/PatientNameTypes';
import {
	FAMILY_NAME_LABEL,
	GIVEN_NAME_LABEL,
	MIDDLE_NAME_LABEL,
	ONE_NAME_ONLY_LABEL,
} from './patientNameDisplay';

/**
 * B1.4-E — the shared other-name component inputs (HI_010_005809 /
 * HI_015_005809), used by both the pre-create collection in the new-patient form
 * and the authenticated create/replace form in the names section.
 *
 * The given/middle/family roles are always labelled explicitly, so which
 * component is the given name and which the family name is never inferred from
 * field order.
 *
 * One-name-only handling mirrors the server contract without weakening it: the
 * single name is captured in the FAMILY-name role, and the given and middle
 * inputs are cleared and disabled while the state is set. The backend re-applies
 * the same rules and remains the authority.
 *
 * Only the five client-suppliable fields exist here. There is no input for
 * ownership, lifecycle type, previous role, provenance, lifecycle timestamps or
 * HI-association state.
 */

const CONTEXT_OPTIONS: Array<{ value: PatientNameContext; label: string }> = [
	{ value: 'general', label: 'General' },
	{ value: 'medicare_card', label: 'Medicare card' },
	{ value: 'preferred', label: 'Preferred' },
];

interface PatientOtherNameFieldsProps {
	value: PatientOtherNameSubmission;
	onChange: (next: PatientOtherNameSubmission) => void;
	/** Prefix for the generated input ids, so several instances stay distinct. */
	idPrefix: string;
	disabled?: boolean;
}

const PatientOtherNameFields: React.FC<PatientOtherNameFieldsProps> = ({
	value,
	onChange,
	idPrefix,
	disabled,
}) => {
	const isOneNameOnly = value.is_one_name_only === true;

	const handleOneNameOnly = (checked: boolean) => {
		onChange(
			checked
				? // Clearing rather than retaining keeps the stored record honest: a
					// one-name-only name has no given or middle component at all.
					{ ...value, is_one_name_only: true, given_name: null, middle_name: null }
				: { ...value, is_one_name_only: false }
		);
	};

	return (
		<Row gutter={[8, 0]}>
			<Col xs={24}>
				<Form.Item label={ONE_NAME_ONLY_LABEL} style={{ marginBottom: 8 }}>
					<Switch
						id={`${idPrefix}-one-name-only`}
						aria-label={ONE_NAME_ONLY_LABEL}
						checked={isOneNameOnly}
						disabled={disabled}
						onChange={handleOneNameOnly}
					/>
				</Form.Item>
			</Col>
			<Col xs={24} md={8}>
				<Form.Item label={GIVEN_NAME_LABEL} style={{ marginBottom: 8 }}>
					<Input
						id={`${idPrefix}-given-name`}
						aria-label={GIVEN_NAME_LABEL}
						value={value.given_name ?? ''}
						disabled={disabled || isOneNameOnly}
						onChange={(e) => onChange({ ...value, given_name: e.target.value })}
					/>
				</Form.Item>
			</Col>
			<Col xs={24} md={8}>
				<Form.Item label={MIDDLE_NAME_LABEL} style={{ marginBottom: 8 }}>
					<Input
						id={`${idPrefix}-middle-name`}
						aria-label={MIDDLE_NAME_LABEL}
						value={value.middle_name ?? ''}
						disabled={disabled || isOneNameOnly}
						onChange={(e) => onChange({ ...value, middle_name: e.target.value })}
					/>
				</Form.Item>
			</Col>
			<Col xs={24} md={8}>
				<Form.Item label={FAMILY_NAME_LABEL} required style={{ marginBottom: 8 }}>
					<Input
						id={`${idPrefix}-family-name`}
						aria-label={FAMILY_NAME_LABEL}
						value={value.family_name ?? ''}
						disabled={disabled}
						onChange={(e) => onChange({ ...value, family_name: e.target.value })}
					/>
				</Form.Item>
			</Col>
			<Col xs={24} md={8}>
				<Form.Item label="Name context" style={{ marginBottom: 8 }}>
					<Select<PatientNameContext>
						id={`${idPrefix}-name-context`}
						aria-label="Name context"
						style={{ width: '100%' }}
						value={value.name_context ?? 'general'}
						options={CONTEXT_OPTIONS}
						disabled={disabled}
						onChange={(next) => onChange({ ...value, name_context: next })}
					/>
				</Form.Item>
			</Col>
		</Row>
	);
};

export default PatientOtherNameFields;
